/**
 * useLabTraceability — Issue #399.
 *
 * Calls `/api/v1/lab/traceability/snapshots` series of endpoints (typed via
 * @jinzhuan/api-client). The page must never fake snapshot data; all rows
 * come from the authenticated backend.
 */
import { useCallback, useState } from 'react';
import {
  createApiClient,
  ApiError as ApiClientError,
  type ApiComponents,
} from '@/lib/adminApiClient';
import { getStoredAccessToken } from '@/lib/tokenStorage';

const apiClient = createApiClient({
  baseUrl: '',
  getAccessToken: () => getStoredAccessToken(),
});

export type LabTraceabilitySnapshotSummaryDto =
  ApiComponents['schemas']['LabTraceabilitySnapshotSummaryDto'];
export type LabTraceabilitySnapshotDetailDto =
  ApiComponents['schemas']['LabTraceabilitySnapshotDetailDto'];
export type LabTraceabilitySnapshotListResponseDto =
  ApiComponents['schemas']['LabTraceabilitySnapshotListResponseDto'];
export type LabTraceabilityCellBatchRowDto =
  ApiComponents['schemas']['LabTraceabilityCellBatchRowDto'];
export type LabTraceabilityMaterialBatchRowDto =
  ApiComponents['schemas']['LabTraceabilityMaterialBatchRowDto'];
export type LabTraceabilityCellBatchListResponseDto =
  ApiComponents['schemas']['LabTraceabilityCellBatchListResponseDto'];
export type LabTraceabilityMaterialBatchListResponseDto =
  ApiComponents['schemas']['LabTraceabilityMaterialBatchListResponseDto'];
export type LabTraceabilitySourceStatusResponseDto =
  ApiComponents['schemas']['LabTraceabilitySourceStatusResponseDto'];

export interface ApiError {
  message: string;
  code?: string;
}

function toApiError(err: unknown): ApiError {
  if (err instanceof ApiClientError) {
    if (err.status === 401)
      return { message: '会话已失效', code: 'UNAUTHENTICATED' };
    if (err.status === 403) return { message: '没有权限', code: 'FORBIDDEN' };
    if (err.status === 404) return { message: '快照不存在', code: 'NOT_FOUND' };
    if (err.status === 400) return { message: '请求参数错误', code: 'BAD_REQUEST' };
  }
  if (err instanceof Error) return { message: err.message };
  return { message: String(err) };
}

export type LabTraceabilitySnapshotStatus =
  | 'draft'
  | 'generated'
  | 'reviewed'
  | 'archived';

export interface SnapshotListFilter {
  readonly status?: LabTraceabilitySnapshotStatus;
  readonly page?: number;
  readonly limit?: number;
}

export function useLabTraceabilitySnapshots() {
  const [data, setData] = useState<LabTraceabilitySnapshotListResponseDto | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchList = useCallback(async (filter: SnapshotListFilter = {}) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.GET('/api/v1/lab/traceability/snapshots', {
        params: {
          query: {
            status: filter.status,
            page: filter.page,
            limit: filter.limit,
          },
        },
      });
      setData(result.data ?? null);
    } catch (err) {
      setError(toApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, loading, error, fetchList };
}

export function useLabTraceabilitySnapshotDetail() {
  const [data, setData] = useState<LabTraceabilitySnapshotDetailDto | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchDetail = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.GET(
        '/api/v1/lab/traceability/snapshots/{id}',
        { params: { path: { id } } },
      );
      setData(result.data ?? null);
    } catch (err) {
      setError(toApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, loading, error, fetchDetail };
}

export function useGenerateLabTraceabilitySnapshot() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const generate = useCallback(
    async (
      notes: string,
    ): Promise<LabTraceabilitySnapshotSummaryDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const result = await apiClient.POST(
          '/api/v1/lab/traceability/snapshots/generate',
          { body: { notes } },
        );
        return result.data ?? null;
      } catch (err) {
        setError(toApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { loading, error, generate };
}

export function useFilterLabTraceabilityCellBatches() {
  const [data, setData] = useState<LabTraceabilityCellBatchListResponseDto | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchRows = useCallback(
    async (
      snapshotId: string,
      filter: { batchNo?: string; cellBatchId?: string },
    ) => {
      setLoading(true);
      setError(null);
      try {
        const result = await apiClient.GET(
          '/api/v1/lab/traceability/snapshots/{id}/cell-batches',
          {
            params: {
              path: { id: snapshotId },
              query: {
                batchNo: filter.batchNo,
                cellBatchId: filter.cellBatchId,
              },
            },
          },
        );
        setData(result.data ?? null);
      } catch (err) {
        setError(toApiError(err));
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { data, loading, error, fetchRows };
}

export function useFilterLabTraceabilityMaterialBatches() {
  const [data, setData] =
    useState<LabTraceabilityMaterialBatchListResponseDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchRows = useCallback(
    async (
      snapshotId: string,
      filter: {
        lotNo?: string;
        materialCode?: string;
        materialBatchId?: string;
        recallStatus?: 'none' | 'flagged' | 'recalled';
      },
    ) => {
      setLoading(true);
      setError(null);
      try {
        const result = await apiClient.GET(
          '/api/v1/lab/traceability/snapshots/{id}/material-batches',
          {
            params: {
              path: { id: snapshotId },
              query: {
                lotNo: filter.lotNo,
                materialCode: filter.materialCode,
                materialBatchId: filter.materialBatchId,
                recallStatus: filter.recallStatus,
              },
            },
          },
        );
        setData(result.data ?? null);
      } catch (err) {
        setError(toApiError(err));
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { data, loading, error, fetchRows };
}

export function useLabTraceabilitySourceStatus() {
  const [data, setData] = useState<LabTraceabilitySourceStatusResponseDto | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchStatus = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.GET(
        '/api/v1/lab/traceability/snapshots/{id}/source-status',
        { params: { path: { id } } },
      );
      setData(result.data ?? null);
    } catch (err) {
      setError(toApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, loading, error, fetchStatus };
}

// Export uses authenticated fetch (typed apiClient.GET expects JSON; the
// export endpoint returns binary/CSV). Auth header carries the same access
// token, matching FinanceAuditViewPage / SalesReportPage.
export async function downloadLabTraceabilityExport(args: {
  snapshotId: string;
  reason: string;
  format?: 'csv' | 'xlsx';
}): Promise<void> {
  const token = getStoredAccessToken();
  if (!token) {
    throw new Error('未登录或会话已失效，请重新登录后再导出');
  }
  const params = new URLSearchParams();
  params.set('reason', args.reason);
  params.set('format', args.format ?? 'csv');
  const response = await fetch(
    `/api/v1/lab/traceability/snapshots/${encodeURIComponent(args.snapshotId)}/export?${params.toString()}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept:
          (args.format ?? 'csv') === 'xlsx'
            ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            : 'text/csv',
      },
      credentials: 'include',
    },
  );
  if (!response.ok) {
    throw new Error(`导出失败：${response.status}`);
  }
  // Defend against soft failures where backend returns 200 with HTML/JSON
  // error body — refuse to treat the response as a downloadable export
  // unless content-type matches the requested format.
  const rawContentType = response.headers.get('content-type') ?? '';
  const contentType = rawContentType.toLowerCase();
  const expectedFormat = args.format ?? 'csv';
  const expectedContentType =
    expectedFormat === 'xlsx'
      ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      : 'text/csv';
  const matchesCsv =
    expectedFormat === 'csv' &&
    (contentType.startsWith('text/csv') ||
      contentType.startsWith('application/csv'));
  const matchesXlsx =
    expectedFormat === 'xlsx' &&
    contentType.startsWith(
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    );
  if (!matchesCsv && !matchesXlsx) {
    throw new Error(
      `导出 content-type 校验失败：HTTP ${response.status}, content-type=${rawContentType || '(empty)'}, expected ${expectedContentType}`,
    );
  }
  const blob = await response.blob();
  const filename =
    extractFilename(response.headers.get('content-disposition')) ??
    `lab-traceability-${args.snapshotId}.${args.format ?? 'csv'}`;
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function extractFilename(headerValue: string | null): string | null {
  if (!headerValue) return null;
  const match = /filename="?([^";]+)"?/.exec(headerValue);
  return match && match[1] ? match[1] : null;
}
